import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect } from 'react';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { usePageTutorial } from '../../hooks/usePageTutorial';
import { titleize } from '../../utils/helpers';
import Confetti from './Confetti';
import LandingPageItems from './LandingPageItems';
import SideBar from './SideBar';
import tutorialSteps from './tutorialSteps';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    zIndex: 10,
    ...theme.sizes.fullPage,
  },
  scroll: {
    ...theme.palette.scrollbar,
    ...theme.palette.primaryBackground,
    width: '100%',
    overflow: 'auto',
    overflowX: 'hidden',
  },
  landingPageContainer: {
    margin: 0,
    paddingLeft: '50px',
    paddingRight: '50px',
    paddingBottom: '15px',
    [theme.breakpoints.down('md')]: {
      paddingBottom: '40px',
      paddingLeft: '35px',
      paddingRight: '35px',
    },
  },
  header: {
    padding: '30px 30px 20px 30px',
    fontWeight: 'bold',
    [theme.breakpoints.down('lg')]: {
      fontSize: '26px',
    },
  },
}));

const LandingPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { setTutorial } = useTutorialContext();
  const { currentUser, refreshProvider } = useCurrentUser();

  const fullName = currentUser ? currentUser.firstName + ' ' + currentUser.lastName : null;

  const displayName = fullName
    ? fullName
        .toLowerCase()
        .split(' ')
        .map(n => (n ? titleize(n) : ''))
        .join(' ')
    : '';

  let imgSize = { width: '86px', height: '86px' };
  const smDevice = useMediaQuery(theme.breakpoints.down('lg'));
  const lgDevice = useMediaQuery(theme.breakpoints.down('xl'));
  if (smDevice) {
    imgSize = { width: '50px', height: '50px' };
  } else if (lgDevice) {
    imgSize = { width: '70px', height: '70px' };
  }
  usePageTutorial({ steps: tutorialSteps });

  // biome-ignore lint/correctness/useExhaustiveDependencies: on mount after login, need to refresh user_query
  useEffect(() => {
    refreshProvider();
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.scroll}>
        <Grid container spacing={1} className={classes.landingPageContainer}>
          <Grid size={12}>
            <Typography variant='h4' className={classes.header}>
              {displayName}, Welcome to your Welligence Dashboard
            </Typography>
            <Divider />
          </Grid>
          {LandingPageItems.map(item => {
            const Component = item.component;
            if (item.released) {
              return (
                <Grid
                  container
                  key={item.label}
                  size={{
                    md: 6,
                    sm: 12,
                  }}
                >
                  <Grid container spacing={2} size={12}>
                    <Grid size={2}>
                      <img
                        id={item.label}
                        src={item.img}
                        alt={item.label}
                        width={imgSize.width}
                        height={imgSize.height}
                      />
                    </Grid>
                    <Grid size={8}>
                      <Typography variant='h6'>{item.label}</Typography>
                      <Divider />
                      <Typography variant='caption'>{item.desc}</Typography>
                    </Grid>
                    {Component ? <Component /> : null}
                  </Grid>
                </Grid>
              );
            } else {
              return null;
            }
          })}
        </Grid>
      </div>
      <SideBar />
      {currentUser?.seenUserWelcome !== undefined && !currentUser?.seenUserWelcome ? (
        <Confetti setTutorial={setTutorial} refreshProvider={refreshProvider} />
      ) : null}
    </div>
  );
};

export default LandingPage;
